/**
 * Force-exit Telegram fullscreen mode.
 *
 * A Main Mini App / direct-link launch (the "Open" button, or opening from the
 * channel list) opens in Bot API 8.0 *fullscreen mode* on recent clients (v8.0+):
 * the webview is edge-to-edge and the ✕ close / ⋮ menu chrome floats OVER the
 * content, so every screen slides underneath it. Launching from inside a chat
 * does not trigger fullscreen, which is why that path looked fine.
 *
 * Our pinned `@twa-dev/sdk` (7.10.1) predates Bot API 8.0 and exposes no
 * `exitFullscreen()` — worse, importing it overwrites the official
 * `telegram-web-app.js` shell that index.html loads, stripping the 8.0 API. So we
 * post the raw client event ourselves. The native client (v9.6) honours
 * `web_app_exit_fullscreen` regardless of which JS shell sits on
 * `window.Telegram.WebApp`. Exiting fullscreen makes Telegram render its normal
 * header bar above the webview, so content no longer collides with the chrome.
 *
 * No-op in-chat (not fullscreen) and on clients that don't understand the event.
 */

interface TelegramTransport {
  TelegramWebviewProxy?: { postEvent?: (eventType: string, eventData: string) => void };
  external?: { notify?: (json: string) => void };
  Telegram?: { WebApp?: { exitFullscreen?: () => void } };
}

/**
 * Send a parameterless event to the Telegram client over whichever transport the
 * current platform exposes (mobile/desktop via TelegramWebviewProxy, Windows via
 * external.notify, web via parent postMessage). Mirrors telegram-web-app.js.
 */
function postTelegramEvent(eventType: string): void {
  const w = window as unknown as TelegramTransport & { parent?: Window };
  try {
    if (w.TelegramWebviewProxy?.postEvent) {
      w.TelegramWebviewProxy.postEvent(eventType, '');
      return;
    }
    if (w.external && typeof w.external.notify === 'function') {
      w.external.notify(JSON.stringify({ eventType, eventData: '' }));
      return;
    }
    // Web clients (k/a): relay to the Telegram parent frame.
    w.parent?.postMessage(JSON.stringify({ eventType, eventData: '' }), 'https://web.telegram.org');
  } catch {
    /* not running inside Telegram, or transport unavailable */
  }
}

/** Exit Telegram fullscreen mode if we're in it. Safe to call repeatedly. */
export function exitTelegramFullscreen(): void {
  const wa = (window as unknown as TelegramTransport).Telegram?.WebApp;
  if (typeof wa?.exitFullscreen === 'function') {
    try {
      wa.exitFullscreen();
      return;
    } catch {
      /* fall through to the raw client event */
    }
  }
  postTelegramEvent('web_app_exit_fullscreen');
}
